import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';

import { ApiError } from '../../shared/api/client.ts';
import { useRevogarLinks } from '../../shared/api/queries.ts';

/**
 * Fecha os links de um cliente antes do prazo.
 *
 * Revoga **todos** os meses de uma vez, e é isso que se quer no caso que faz
 * esta tela existir: o cliente trocou de contabilidade, e os links que estão
 * na caixa do contador antigo precisam parar de abrir. Revogar mês a mês
 * deixaria algum para trás, e o que ficou para trás é documento fiscal
 * acessível por quem não cuida mais daquela loja.
 *
 * O motivo é obrigatório porque fica guardado no acesso: meses depois, "este
 * link não abre" precisa ter resposta.
 */
export function DialogoRevogarLinks({
  cliente,
  aoFechar,
  aoRevogado,
}: {
  /** `null` mantém o diálogo fechado. */
  cliente: { empresaId: string; razaoSocial: string } | null;
  aoFechar: () => void;
  aoRevogado: (revogados: number) => void;
}) {
  const revogar = useRevogarLinks();
  const [motivo, setMotivo] = useState('');

  async function confirmar() {
    if (cliente === null) return;

    try {
      const saida = await revogar.mutateAsync({ empresaId: cliente.empresaId, motivo });
      aoRevogado(saida.revogados);
      setMotivo('');
      aoFechar();
    } catch {
      // A recusa fica à vista, em `revogar.error`.
    }
  }

  return (
    <Dialog open={cliente !== null} onClose={aoFechar} fullWidth maxWidth="sm">
      <DialogTitle>Revogar os links de {cliente?.razaoSocial}</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2">
            Todos os links deste cliente param de abrir na hora, de todos os meses. Os
            arquivos continuam no sistema, e um envio novo emite um link novo.
          </Typography>

          <TextField
            label="Motivo"
            value={motivo}
            onChange={(evento) => setMotivo(evento.target.value)}
            placeholder="Trocou de contabilidade"
            helperText="Fica registrado no acesso revogado."
            fullWidth
            autoFocus
          />

          {revogar.isError && <Alert severity="error">{mensagemDoErro(revogar.error)}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={aoFechar}>Cancelar</Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<LinkOffOutlinedIcon />}
          onClick={() => void confirmar()}
          disabled={motivo.trim().length < 3}
          loading={revogar.isPending}
        >
          Revogar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function mensagemDoErro(erro: unknown): string {
  if (erro instanceof ApiError) return erro.message;
  return erro instanceof Error ? erro.message : 'Falha inesperada.';
}
